// Breakdown-pivot dimensions + stat-column catalogue (Phase 3a finish) — PURE.
//
// The explorer's stat columns (the same catalogue the retired campaign table
// offered) render + sort off a rolled-up NodeMetrics block. RATIOS are recomputed
// from the summed numerator/denominator inside the rollup builder
// (breakdownGrouping.finalize) — never averaged; this file only reads them back
// out for sorting/CSV. Money (spend + money ratios) is PER-CURRENCY; counts + pct
// ratios are single numbers. reach/frequency are absent (non-additive — omitted
// server-side).

import type { CurrencyAmount, ReportingBreakdownRow, ReportingDimension } from '../../types/reporting';
import { classifyCampaignType } from '../../lib/reportingCampaignTypes';
import type { BreakdownRowView, NodeMetrics } from './breakdownGrouping';

// The dimension label + the chip order (fixed tree depth).
export const DIMENSION_LABEL: Record<ReportingDimension, string> = {
  account: 'Account',
  campaign: 'Campaign',
  adset: 'Ad set',
  ad: 'Ad',
};

export const DIMENSION_ORDER: ReportingDimension[] = ['account', 'campaign', 'adset', 'ad'];

// Canonical depth order + dedupe (client mirror of the server's groupByQuery
// helper) — the chips select any subset; the pivot renders + persists in tree
// depth order so the staircase and the server GROUP BY agree.
export function canonicalizeGroupBy(
  groupBy: readonly ReportingDimension[],
): ReportingDimension[] {
  return DIMENSION_ORDER.filter((d) => groupBy.includes(d));
}

// The id + name a leaf breakdown row contributes at a given dimension (the rollup
// builder keys nodes on the id + labels them with the name).
export function dimId(row: ReportingBreakdownRow, dim: ReportingDimension): string | null {
  switch (dim) {
    case 'account':
      return row.accountId;
    case 'campaign':
      return row.campaignId;
    case 'adset':
      return row.adsetId;
    case 'ad':
      return row.adId;
  }
}

export function dimName(row: ReportingBreakdownRow, dim: ReportingDimension): string | null {
  switch (dim) {
    case 'account':
      return row.accountName;
    case 'campaign':
      return row.campaignName;
    case 'adset':
      return row.adsetName;
    case 'ad':
      return row.adName;
  }
}

// ---- stat columns (the approved reporting catalogue) -----------------------

export type BreakdownStatKey =
  | 'spend'
  | 'totalLeads'
  | 'metaLeads'
  | 'websiteLeads'
  | 'cpl'
  | 'costPerMetaLead'
  | 'linkCtr'
  | 'impressions'
  | 'clicks'
  | 'ctrAll'
  | 'cpcAll'
  | 'cpm'
  | 'costPerLinkClick'
  | 'landingPageViews'
  | 'engagement';

// A stat's value kind — drives formatting (money → per-currency; count → int;
// pct → 2dp %). money covers both raw money (spend) and money ratios (both are
// CurrencyAmount[]).
export type StatKind = 'money' | 'count' | 'pct';

export interface BreakdownStatColumn {
  key: BreakdownStatKey;
  label: string;
  kind: StatKind;
}

// Factory order = catalogue order (default block first, then optional). Labels
// mirror the campaign table's catalogue (the columns Scott approved).
export const BREAKDOWN_STAT_COLUMNS: BreakdownStatColumn[] = [
  { key: 'spend', label: 'Spend', kind: 'money' },
  { key: 'totalLeads', label: 'Leads', kind: 'count' },
  { key: 'metaLeads', label: 'Meta Leads', kind: 'count' },
  { key: 'websiteLeads', label: 'Website Leads', kind: 'count' },
  { key: 'cpl', label: 'Cost / Lead', kind: 'money' },
  { key: 'costPerMetaLead', label: 'Cost / Meta Lead', kind: 'money' },
  { key: 'linkCtr', label: 'Link CTR', kind: 'pct' },
  { key: 'impressions', label: 'Impressions', kind: 'count' },
  { key: 'clicks', label: 'Clicks (all)', kind: 'count' },
  { key: 'ctrAll', label: 'CTR (all)', kind: 'pct' },
  { key: 'cpcAll', label: 'CPC (all)', kind: 'money' },
  { key: 'cpm', label: 'CPM', kind: 'money' },
  { key: 'costPerLinkClick', label: 'Cost / Link Click', kind: 'money' },
  { key: 'landingPageViews', label: 'Landing Page Views', kind: 'count' },
  { key: 'engagement', label: 'Engagement', kind: 'count' },
];

// v1 default-visible: the first 8 (the rest are opt-in via the Columns menu).
export const BREAKDOWN_DEFAULT_STAT_COLUMNS: BreakdownStatKey[] = [
  'spend',
  'totalLeads',
  'metaLeads',
  'websiteLeads',
  'cpl',
  'costPerMetaLead',
  'linkCtr',
  'impressions',
];

export const BREAKDOWN_STAT_ORDER: BreakdownStatKey[] = BREAKDOWN_STAT_COLUMNS.map((c) => c.key);
const ALL_STAT_KEYS = new Set<BreakdownStatKey>(BREAKDOWN_STAT_ORDER);
const KIND_BY_KEY: Record<BreakdownStatKey, StatKind> = Object.fromEntries(
  BREAKDOWN_STAT_COLUMNS.map((c) => [c.key, c.kind]),
) as Record<BreakdownStatKey, StatKind>;

export function statKind(key: BreakdownStatKey): StatKind {
  return KIND_BY_KEY[key];
}

// Sanitise a stored visible-column list: drop unknowns, dedupe, order by the
// factory sequence; empty → the default set (never an empty pivot).
export function sanitizeStatColumns(stored: readonly string[]): BreakdownStatKey[] {
  const set = new Set<BreakdownStatKey>();
  for (const k of stored) {
    if (ALL_STAT_KEYS.has(k as BreakdownStatKey)) set.add(k as BreakdownStatKey);
  }
  const ordered = BREAKDOWN_STAT_ORDER.filter((k) => set.has(k));
  return ordered.length > 0 ? ordered : [...BREAKDOWN_DEFAULT_STAT_COLUMNS];
}

// ---- reading rolled metrics back out (render / sort / CSV) -----------------

const sumAmounts = (a: CurrencyAmount[]): number => a.reduce((n, x) => n + x.amount, 0);

// The per-currency money array for a money-kind stat.
export function statMoney(m: NodeMetrics, key: BreakdownStatKey): CurrencyAmount[] {
  switch (key) {
    case 'spend':
      return m.spend;
    case 'cpl':
      return m.cpl;
    case 'costPerMetaLead':
      return m.costPerMetaLead;
    case 'cpcAll':
      return m.cpcAll;
    case 'cpm':
      return m.cpm;
    case 'costPerLinkClick':
      return m.costPerLinkClick;
    default:
      return [];
  }
}

export function statCount(m: NodeMetrics, key: BreakdownStatKey): number {
  switch (key) {
    case 'totalLeads':
      return m.totalLeads;
    case 'metaLeads':
      return m.metaLeads;
    case 'websiteLeads':
      return m.websiteLeads;
    case 'impressions':
      return m.impressions;
    case 'clicks':
      return m.clicks;
    case 'landingPageViews':
      return m.landingPageViews;
    case 'engagement':
      return m.engagement;
    default:
      return 0;
  }
}

export function statPct(m: NodeMetrics, key: BreakdownStatKey): number | null {
  switch (key) {
    case 'linkCtr':
      return m.linkCtr;
    case 'ctrAll':
      return m.ctrAll;
    default:
      return null;
  }
}

// A single comparable value for sorting siblings by a stat column. Money sums its
// per-currency amounts as a sort proxy (groups are single-currency in practice);
// a money ratio with no defined currency (empty array) sorts null (last).
export function statSortValue(m: NodeMetrics, key: BreakdownStatKey): number | null {
  const kind = statKind(key);
  if (kind === 'count') return statCount(m, key);
  if (kind === 'pct') return statPct(m, key);
  const money = statMoney(m, key);
  return money.length > 0 ? sumAmounts(money) : null;
}

// ---- attribute columns (Budget / delivery status / Objective type) ---------
//
// Non-metric columns: aggregated as the latest value server-side, rendered per the
// approved visual treatment (dot / chip / compact value) on the entity's NATIVE
// level only. Opt-in via the Columns menu; unlike metrics they do NOT go through
// the numeric sort path (attribute headers are non-sortable — brief §Sorting).

// Delivery STATUS is no longer a column — it rides the dimension name as a
// coloured dot (see nameStatus + renderBreakdownDimCell). Budget + Type stay.
export type BreakdownAttributeKey = 'budget' | 'objective';

export type BreakdownColumnKey = BreakdownStatKey | BreakdownAttributeKey;
export type ColumnKind = StatKind | 'attribute';
export type AttributeType = 'budget' | 'objective';

export interface BreakdownColumn {
  key: BreakdownColumnKey;
  label: string;
  kind: ColumnKind;
  attrType?: AttributeType; // attribute columns only
  // objective → 'campaign'. Budget is dynamic (the budgetLevel-owning level) so
  // it has no fixed nativeLevel.
  nativeLevel?: ReportingDimension;
}

export const BREAKDOWN_ATTRIBUTE_COLUMNS: BreakdownColumn[] = [
  { key: 'budget', label: 'Budget', kind: 'attribute', attrType: 'budget' },
  { key: 'objective', label: 'Type', kind: 'attribute', attrType: 'objective', nativeLevel: 'campaign' },
];

// The unified catalogue: the 15 metric columns, then the 5 attribute columns.
export const BREAKDOWN_COLUMNS: BreakdownColumn[] = [
  ...BREAKDOWN_STAT_COLUMNS.map(
    (c): BreakdownColumn => ({ key: c.key, label: c.label, kind: c.kind }),
  ),
  ...BREAKDOWN_ATTRIBUTE_COLUMNS,
];

export const BREAKDOWN_COLUMN_ORDER: BreakdownColumnKey[] = BREAKDOWN_COLUMNS.map((c) => c.key);
const ALL_COLUMN_KEYS = new Set<BreakdownColumnKey>(BREAKDOWN_COLUMN_ORDER);
const COLUMN_BY_KEY = new Map<BreakdownColumnKey, BreakdownColumn>(
  BREAKDOWN_COLUMNS.map((c) => [c.key, c]),
);

export function columnByKey(key: BreakdownColumnKey): BreakdownColumn | undefined {
  return COLUMN_BY_KEY.get(key);
}

// Whether a column key is a metric (vs an attribute) — metric columns are
// sortable + go through the NodeMetrics renderers; attributes don't.
export function isStatKey(key: BreakdownColumnKey): key is BreakdownStatKey {
  return ALL_STAT_KEYS.has(key as BreakdownStatKey);
}

// Sanitise a stored visible-column list over the FULL catalogue (stats + attrs);
// empty → the default set. Supersedes sanitizeStatColumns for the explorer prefs.
export function sanitizeColumns(stored: readonly string[]): BreakdownColumnKey[] {
  const set = new Set<BreakdownColumnKey>();
  for (const k of stored) {
    if (ALL_COLUMN_KEYS.has(k as BreakdownColumnKey)) set.add(k as BreakdownColumnKey);
  }
  const ordered = BREAKDOWN_COLUMN_ORDER.filter((k) => set.has(k));
  return ordered.length > 0 ? ordered : [...BREAKDOWN_DEFAULT_STAT_COLUMNS];
}

// The load-bearing render rule: an attribute renders ONLY on its native level and
// ONLY when the row carries attributes (Flat leaves + every Tree node; blank on
// rollup subtotals). Budget's native level is dynamic — the budgetLevel-owning
// level (campaign for CBO, ad-set for ABO).
export function attributeVisibleOn(row: BreakdownRowView, column: BreakdownColumn): boolean {
  const attrs = row.attributes;
  if (!attrs || column.kind !== 'attribute') return false;
  switch (column.attrType) {
    case 'objective':
      return row.dim === 'campaign';
    case 'budget':
      return attrs.budgetLevel != null && row.dim === attrs.budgetLevel;
    default:
      return false;
  }
}

// The delivery status a name cell should carry as a coloured dot, for the GIVEN
// dimension — `campaign` → campaignStatus, `adset` → adsetStatus, `ad` → adStatus
// (account has no delivery status). Each grouped level's name cell passes its OWN
// dim, so the Flat pivot shows campaign + ad-set + ad dots each on their level's
// row (not just the deepest). Null when that level's status is absent (a level
// deeper than the source grain, an unattributed/rollup row above it, or blank).
// Returns the raw status word (the dot's colour + hover tooltip derive from it
// via statusRole).
export function nameStatus(row: BreakdownRowView, dim: ReportingDimension): string | null {
  const s =
    dim === 'campaign'
      ? row.campaignStatus
      : dim === 'adset'
        ? row.adsetStatus
        : dim === 'ad'
          ? row.adStatus
          : null; // account
  return s && s.trim() !== '' ? s : null;
}

// The semantic role a delivery status maps to (drives the status-dot colour).
// green = active/delivering, grey = paused, amber = limited/in-review/pending,
// red = rejected/disapproved/error. Tolerant of Meta variants; unknown → grey.
export type StatusRole = 'active' | 'paused' | 'review' | 'rejected' | 'unknown';

export function statusRole(status: string | null): StatusRole {
  const s = (status ?? '').toLowerCase();
  if (s === '') return 'unknown';
  if (s === 'active') return 'active';
  if (/reject|disapprov|denied|error/.test(s)) return 'rejected';
  if (/review|pending|limited|learning/.test(s)) return 'review';
  if (/paused|archiv|inactive|off|deleted|not_delivering/.test(s)) return 'paused';
  return 'unknown';
}

// The muted daily/lifetime tag for a budget value.
export function budgetUnitTag(budgetType: string | null): string {
  return budgetType === 'lifetime' ? '/life' : '/day';
}

// The plain-text value of an attribute on a row (for CSV) — '' when not visible on
// this row. Status → the word; Type → the short class label; Budget → "12.50/day".
export function attributeText(row: BreakdownRowView, column: BreakdownColumn): string {
  if (!attributeVisibleOn(row, column)) return '';
  const attrs = row.attributes!;
  switch (column.attrType) {
    case 'objective':
      return classifyCampaignType(attrs.objective, attrs.optimizationGoal).label;
    case 'budget': {
      if (attrs.budgetAmount == null) return '';
      const amount = Math.round(attrs.budgetAmount * 100) / 100;
      return `${amount}${budgetUnitTag(attrs.budgetType)}`;
    }
    default:
      return '';
  }
}
